import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/components/LevelUpModal.css";

const BADGE_BASE_PATH = "/badges/";
const BADGE_EXTENSION = ".png";
const LEVEL_UP_SOUND_PATH = "/sound-efects/level-up.mp3";

function buildBadgeUrl(badgeKey) {
  if (!badgeKey) return "";

  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}${BADGE_BASE_PATH}${String(badgeKey)
    .trim()
    .toLowerCase()}${BADGE_EXTENSION}`;
}

export default function LevelUpModal({
  level,
  rankName,
  badgeKey,
  hasRankChanged = false,
  onClose,
}) {
  const { t } = useTranslation();
  const [isBadgeMissing, setIsBadgeMissing] = useState(false);
  const badgeUrl = useMemo(() => buildBadgeUrl(badgeKey), [badgeKey]);

  useEffect(() => {
    setIsBadgeMissing(false);
  }, [badgeUrl]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    const audio = new Audio(
      `${process.env.PUBLIC_URL || ""}${LEVEL_UP_SOUND_PATH}`
    );
    audio.volume = 0.85;
    audio.play().catch(() => {});

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  return (
    <div className="level-up-modal-overlay" onClick={onClose}>
      <div
        className="level-up-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t("level_up_dialog_label", {
          defaultValue: "Level up celebration",
        })}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="level-up-modal-close"
          onClick={onClose}
        >
          {t("close", { defaultValue: "Close" })}
        </button>

        <div className="level-up-modal-kicker">
          {t("level_up_kicker", {
            defaultValue: "Progression update",
          })}
        </div>

        <h3 className="level-up-modal-title">
          {t("level_up_title", {
            defaultValue: "Level Up",
          })}
        </h3>

        <p className="level-up-modal-body">
          {t("level_up_body", {
            defaultValue:
              "Well done. You've reached Level {{level}} and unlocked a new rank badge.",
            level,
          })}
        </p>

        {hasRankChanged && rankName ? (
          <p className="level-up-modal-rank">
            {t("level_up_rank_line", {
              defaultValue: "Your new rank is {{rank}}.",
              rank: rankName,
            })}
          </p>
        ) : null}

        {badgeUrl && !isBadgeMissing ? (
          <img
            src={badgeUrl}
            alt={t("level_up_badge_alt", {
              defaultValue: "New rank badge",
            })}
            className="level-up-modal-badge"
            onError={() => setIsBadgeMissing(true)}
          />
        ) : null}

        <div className="level-up-modal-pill">
          {t("progression_level_label", {
            defaultValue: "Level {{level}}",
            level,
          })}
        </div>
      </div>
    </div>
  );
}
